import * as React from 'react';
import { useInView } from '@cian/valuation-utils-component';

import { Carousel } from '../Carousel';
import { Underground } from '../GeoInformation/components';
import { PriceDiscount } from '../PriceDiscount';
import type { IRecommendationDesktopProps } from '../RecommendationDesktop';
import { ClosestGeo } from './ClosestGeo';
import * as styles from './RecommendationDesktop.css';

const CALLBACK_DELAY = 1000;

export const RecommendationDesktop: React.FC<IRecommendationDesktopProps> = ({
  onClick,
  onShowTrap,
  offerId,
  analyticsData,
  href,
  imageUrl,
  title: price,
  description,
  transport: address,
  underground,
  closestGeo,
  regionId,
  favoriteButton,
  labels,
  onTrack,
  position,
  discount,
  contextToken,
}) => {
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  const [trapShown, setTrapShown] = React.useState(false);
  const timerRef = React.useRef<number | null>(null);
  const [isHovered, setHovered] = React.useState(false);

  const photos = React.useMemo(() => [{ id: 1, url: imageUrl }], [imageUrl]);

  const handleMouseEnter = React.useCallback(() => setHovered(true), []);
  const handleMouseLeave = React.useCallback(() => setHovered(false), []);
  const handleClick = React.useCallback(() => onClick(analyticsData), [analyticsData, onClick]);

  React.useEffect(() => {
    if (!offerId && inView && !trapShown) {
      onShowTrap(analyticsData);
      setTrapShown(true);
    }

    if (inView) {
      timerRef.current = window.setTimeout(() => {
        onTrack(offerId, position, contextToken);

        if (timerRef.current !== null) {
          window.clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }, CALLBACK_DELAY);

      return () => {
        if (timerRef.current !== null) {
          window.clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }

    return undefined;
  }, [inView, offerId, analyticsData, onShowTrap, trapShown, onTrack, position, contextToken]);

  return (
    <article
      ref={ref}
      className={styles['container']}
      data-name="RedesignedRecommendationDesktop"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles['photo']}>
        <a
          className={styles['photo-link']}
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={handleClick}
          aria-label={description}
        >
          <div className={styles['gallery']}>
            <Carousel media={photos} desktopControls={isHovered} />
          </div>
        </a>
        {labels}
        <div className={styles['favorite']}>{favoriteButton}</div>
      </div>

      <a className={styles['content-link']} href={href} target="_blank" rel="noreferrer" onClick={handleClick}>
        <div>
          <div className={styles['price']}>
            <span className={styles['price-value']}>{discount ? discount.newPrice : price}</span>
            {discount && (
              <PriceDiscount className={styles['discount']} oldPrice={discount.oldPrice} percent={discount.percent} />
            )}
          </div>
          <span className={styles['description']}>{description}</span>
        </div>
        <div className={styles['location']}>
          {closestGeo ? (
            <ClosestGeo closestGeo={closestGeo} regionId={regionId} />
          ) : (
            underground && (
              <span className={styles['geo']}>
                <Underground underground={underground} regionId={regionId} isRedesign={true} />
              </span>
            )
          )}
          {address && <span className={styles['address']}>{address}</span>}
        </div>
      </a>
    </article>
  );
};
